import posthog from "posthog-js";

import { isRTKError } from "utils/errors/isRTKError";

import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";

import type { ObjectValuesAsType } from "utils/types";

import type { POSTHOG_EVENTS } from "./events";

type PosthogEvent = ObjectValuesAsType<typeof POSTHOG_EVENTS>;

export interface CaptureEventProperties {
  [key: string]: unknown;
  entityId?: string;
  errorCode?: number | string;
  errorMessage?: string;
  errorName?: string;
  flow?: string;
  statusCode?: number | string;
}

export function captureEvent(
  event: PosthogEvent,
  properties?: CaptureEventProperties
) {
  try {
    posthog.capture(event, properties);
  } catch {
    // ignore analytics errors
  }
}

export function extractErrorProperties(error: unknown): CaptureEventProperties {
  if (!error) {
    return {};
  }

  if (isRTKError(error as FetchBaseQueryError | SerializedError)) {
    const rtkError = error as FetchBaseQueryError & {
      data: { exception: { code: number; message: string } };
    };

    return {
      statusCode: rtkError.status,
      errorCode: rtkError.data.exception.code,
      errorMessage: rtkError.data.exception.message,
    };
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    "data" in error
  ) {
    const baseQueryError = error as FetchBaseQueryError;

    return {
      statusCode: baseQueryError.status,
    };
  }

  if (error instanceof Error) {
    return {
      errorName: error.name,
      errorMessage: error.message,
    };
  }

  return {};
}
